import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, AlertTriangle, ChevronLeft, ChevronRight, RotateCcw, Eye, Wrench, ArrowUp, RotateCw, MoveRight } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { ProgressBar } from './ProgressBar';
import { SafetyBadge } from './SafetyBadge';
import { RepairVisualizer } from './RepairVisualizer';
import { cn } from '../../lib/utils';

// Visual Guide Component - shows animated diagram based on step
const VisualGuide = ({ step, deviceType }) => {
  const instruction = (step?.instruction || '').toLowerCase();

  // Determine motion type and icon
  let motionType = 'focus';
  let MotionIcon = Eye;

  if (instruction.includes('rotate') || instruction.includes('turn') || instruction.includes('twist')) {
    motionType = 'rotate';
    MotionIcon = RotateCw;
  } else if (instruction.includes('pull') || instruction.includes('remove') || instruction.includes('lift')) {
    motionType = 'pull';
    MotionIcon = ArrowUp;
  } else if (instruction.includes('slide') || instruction.includes('push') || instruction.includes('insert')) {
    motionType = 'slide';
    MotionIcon = MoveRight;
  } else if (instruction.includes('replace') || instruction.includes('install') || instruction.includes('fix')) {
    motionType = 'replace';
    MotionIcon = Wrench;
  }

  // Determine what part is being worked on
  let targetPart = 'component';
  const partKeywords = {
    'glass': 'Glass Pane', 'window': 'Window', 'pane': 'Glass',
    'cover': 'Cover', 'lid': 'Lid', 'panel': 'Panel',
    'screw': 'Screws', 'bolt': 'Bolts', 'nut': 'Nuts',
    'wire': 'Wiring', 'cable': 'Cable', 'connector': 'Connector',
    'motor': 'Motor', 'bearing': 'Bearing', 'fan': 'Fan',
    'screen': 'Screen', 'display': 'Display', 'button': 'Button',
    'frame': 'Frame', 'case': 'Case', 'housing': 'Housing',
    'seal': 'Seal', 'gasket': 'Gasket', 'rubber': 'Rubber',
    'glove': 'Safety Gear', 'goggle': 'Safety Gear', 'safety': 'Safety'
  };

  for (const [keyword, part] of Object.entries(partKeywords)) {
    if (instruction.includes(keyword)) {
      targetPart = part;
      break;
    }
  }

  return (
    <div className="bg-black/90 border border-cyan-500/30 rounded-lg h-full flex flex-col">
      {/* Header */}
      <div className="p-3 border-b border-cyan-500/20">
        <span className="font-mono text-xs text-cyan-400 uppercase">Visual Guide</span>
      </div>

      {/* Animation Area */}
      <div className="flex-1 flex items-center justify-center p-4 relative overflow-hidden min-h-[200px]">
        {/* Grid Background */}
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'linear-gradient(rgba(0,240,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,240,255,0.5) 1px, transparent 1px)',
            backgroundSize: '25px 25px'
          }}
        />

        {/* Central Device SVG */}
        <div className="relative z-10">
          <svg width="160" height="120" viewBox="0 0 160 120">
            {/* Device Outline */}
            <rect x="10" y="10" width="140" height="100" rx="4"
              fill="#1a1a1a" stroke="#333" strokeWidth="2" />

            {/* Inner Panel */}
            <rect x="25" y="25" width="110" height="70" rx="2"
              fill="#252525" stroke="#444" strokeWidth="1" />

            {/* Target Highlight */}
            <motion.rect
              x="40" y="35" width="80" height="50" rx="2"
              fill="none" stroke="#FF5F1F" strokeWidth="2"
              strokeDasharray="8 4"
              animate={{
                strokeDashoffset: [0, 24],
                opacity: [0.6, 1, 0.6]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />

            {/* Pulsing Dot */}
            <motion.circle
              cx="80" cy="60" r="6"
              fill="#FF5F1F"
              animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </svg>

          {/* Animated Action Icon */}
          <motion.div
            className="absolute top-0 right-0 transform translate-x-2 -translate-y-2"
            animate={
              motionType === 'rotate' ? { rotate: [0, 360] } :
                motionType === 'pull' ? { y: [0, -10, 0] } :
                  motionType === 'slide' ? { x: [0, 10, 0] } :
                    { scale: [1, 1.1, 1] }
            }
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="bg-orange-500 p-2 rounded-full shadow-lg shadow-orange-500/40">
              <MotionIcon className="w-5 h-5 text-white" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Info Footer */}
      <div className="p-3 border-t border-cyan-500/20 space-y-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded font-mono uppercase">
            {motionType}
          </span>
          <span className="text-gray-400">→</span>
          <span className="text-cyan-400 font-mono">{targetPart}</span>
        </div>
      </div>
    </div>
  );
};

export const RepairWizard = ({
  steps = [],
  onComplete,
  onReset,
  safetyRisk = 'low',
  deviceType = 'electrical',
  sessionId,
  className
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);

  // Centralized visual cache - stores all generated visuals for this session
  const visualsCacheRef = useRef({});
  // Track which steps are currently being fetched (prevents duplicate requests)
  const loadingStepsRef = useRef(new Set());
  // Force re-render when cache updates
  const [cacheVersion, setCacheVersion] = useState(0);

  // Throttling State
  const visualQueueRef = useRef([]);
  const isProcessingQueueRef = useRef(false);
  const queueProcessTimeoutRef = useRef(null);
  const MIN_DELAY_MS = 4500; // 4.5 seconds between calls to stay under 15 RPM

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  /**
   * Process the next item in the visual generation queue
   */
  const processQueue = useCallback(async () => {
    if (isProcessingQueueRef.current || visualQueueRef.current.length === 0) {
      return;
    }

    isProcessingQueueRef.current = true;
    const { stepIndex, resolve } = visualQueueRef.current.shift();

    // Note: step is already marked as loading in queueStepVisual

    try {
      if (!sessionId) {
        resolve(null);
        return;
      }

      const response = await fetch(
        `${API_BASE}/generate-step-visual/${sessionId}/${stepIndex + 1}`,
        { method: 'POST' }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.html_content) {
          visualsCacheRef.current[stepIndex] = data.html_content;
        }
      }
      resolve(visualsCacheRef.current[stepIndex] || null);
    } catch (err) {
      console.error("Visual generation failed:", err);

      // DEBUGGING: Always show error toast to understand why it failed
      let errorMessage = "Visual generation failed";
      if (err.response) {
        try {
          const errorData = await err.response.json();
          errorMessage = `API Error ${err.status}: ${errorData.detail || err.statusText}`;
        } catch (e) {
          errorMessage = `API Error ${err.status}: ${err.statusText}`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      toast.error(errorMessage);
      resolve(null);
    } finally {
      loadingStepsRef.current.delete(stepIndex);
      setCacheVersion(v => v + 1);

      // Wait before processing next item to respect rate limit
      queueProcessTimeoutRef.current = setTimeout(() => {
        isProcessingQueueRef.current = false;
        processQueue();
      }, MIN_DELAY_MS);
    }
  }, [sessionId, API_BASE]);

  /**
   * Queue a visual generation request
   */
  const queueStepVisual = useCallback((stepIndex) => {
    return new Promise((resolve) => {
      // Check if already cached
      if (visualsCacheRef.current[stepIndex]) {
        resolve(visualsCacheRef.current[stepIndex]);
        return;
      }
      // Check if already loading/queued
      if (loadingStepsRef.current.has(stepIndex)) {
        resolve(null); // Or we could start tracking promises if we wanted to be fancy, but null is safe
        return;
      }

      // Mark as loading IMMEDIATELY so UI shows "Generating..." instead of fallback
      loadingStepsRef.current.add(stepIndex);
      setCacheVersion(v => v + 1);

      // Add to queue
      visualQueueRef.current.push({ stepIndex, resolve });
      processQueue();
    });
  }, [processQueue]);

  // Fetch current step visual and preload next
  useEffect(() => {
    // Priority: Current step
    if (!visualsCacheRef.current[currentStep]) {
      // Prioritize current step by putting it at the front if possible, 
      // but for simplicity we just push. The user usually stays on a step > 4.5s.
      // If we wanted true LIFO for user navigation, we'd manipulate the array.
      // For now, FIFO is acceptable as it ensures steps generate in order.
      queueStepVisual(currentStep);
    }

    // Preload next step (N+1) with a delay to let current step finish
    // We queue it immediately, the queue processor handles the spacing.
    const nextStep = currentStep + 1;
    if (nextStep < steps.length && !visualsCacheRef.current[nextStep]) {
      queueStepVisual(nextStep);
    }

    return () => {
      // No cleanup needed for queue logic specifically, let it process
    };
  }, [currentStep, steps.length, queueStepVisual]);

  // Clean timeouts on unmount
  useEffect(() => {
    return () => {
      if (queueProcessTimeoutRef.current) {
        clearTimeout(queueProcessTimeoutRef.current);
      }
    };
  }, []);

  // Clear cache on reset
  const handleReset = useCallback(() => {
    visualsCacheRef.current = {};
    loadingStepsRef.current.clear();
    visualQueueRef.current = []; // Clear queue
    setCacheVersion(v => v + 1);
    onReset?.();
  }, [onReset]);

  const handleNext = () => {
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps([...completedSteps, currentStep]);
    }
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete?.();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const step = steps[currentStep];

  if (!steps.length) {
    return (
      <div className="text-center py-12 text-text-muted">
        No repair steps available.
      </div>
    );
  }

  return (
    <div className={cn('w-full max-w-5xl mx-auto space-y-4 sm:space-y-6 px-2 sm:px-0 no-overflow', className)} data-testid="repair-wizard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="font-heading text-lg sm:text-xl font-bold uppercase tracking-tight text-white">
          REPAIR PROCEDURE
        </h2>
        <SafetyBadge risk={safetyRisk} />
      </div>

      {/* Progress */}
      <ProgressBar steps={steps} currentStep={currentStep} />

      {/* Responsive Layout: Mobile (stacked, visual first) | Tablet/Desktop (side-by-side) */}
      <div className="flex flex-col md:grid md:grid-cols-2 gap-4 md:gap-6">
        {/* Visual Guide - appears first on mobile, left on tablet+ */}
        <div className="order-first md:order-none visual-container">
          <RepairVisualizer
            step={step}
            stepNumber={currentStep + 1}
            sessionId={sessionId}
            deviceType={deviceType}
            htmlContent={visualsCacheRef.current[currentStep] || null}
            isLoading={loadingStepsRef.current.has(currentStep)}
          />
        </div>

        {/* Step Instructions - stacks below visual on mobile */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="bg-surface border border-border p-4 sm:p-5 space-y-4 flex flex-col step-card-mobile md:max-h-[60vh] md:overflow-y-auto"
          >
            {/* Step number and title */}
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-12 h-12 flex items-center justify-center font-mono text-xl font-bold",
                completedSteps.includes(currentStep)
                  ? "bg-success text-black"
                  : "bg-primary text-white"
              )}>
                {completedSteps.includes(currentStep) ? (
                  <Check className="w-6 h-6" />
                ) : (
                  step?.step || currentStep + 1
                )}
              </div>
              <div>
                <span className="font-mono text-xs text-text-muted">STEP {currentStep + 1} OF {steps.length}</span>
                <div className="text-cyan-400 font-mono text-xs mt-0.5">
                  {Math.round(((currentStep + 1) / steps.length) * 100)}% COMPLETE
                </div>
              </div>
            </div>

            {/* Instruction */}
            <div className="flex-1">
              <p className="text-white text-lg leading-relaxed" data-testid="step-instruction">
                {step?.instruction}
              </p>
            </div>

            {/* Safety note */}
            {step?.safety_note && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-warning/10 border border-warning/30 p-4 flex items-start gap-3"
              >
                <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                <p className="text-sm text-warning/90" data-testid="step-safety-note">
                  {step.safety_note}
                </p>
              </motion.div>
            )}

            {/* Navigation Buttons - responsive layout */}
            <div className="step-navigator pt-4 border-t border-border">
              {/* Secondary actions */}
              <div className="flex gap-2 order-2 sm:order-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrev}
                  disabled={currentStep === 0}
                  className="flex-1 sm:flex-initial border-border hover:border-primary disabled:opacity-50 min-h-[44px]"
                  data-testid="prev-step-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  <span className="hidden xs:inline">Previous</span>
                  <span className="xs:hidden">Prev</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReset}
                  className="flex-1 sm:flex-initial border-border hover:border-destructive text-text-muted hover:text-destructive min-h-[44px]"
                  data-testid="reset-wizard-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  <span className="hidden xs:inline">Start Over</span>
                  <span className="xs:hidden">Reset</span>
                </Button>
              </div>

              {/* Primary action - full width on mobile, comes first visually */}
              <Button
                onClick={handleNext}
                className="w-full sm:w-auto order-1 sm:order-2 bg-primary text-white hover:bg-primary/90 font-heading uppercase px-6 min-h-[48px] sm:min-h-[44px] text-base"
                data-testid="next-step-btn"
              >
                {currentStep === steps.length - 1 ? (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Complete Repair
                  </>
                ) : (
                  <>
                    NEXT STEP
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* View All Steps - Below both columns */}
      <details className="group bg-surface/50 border border-border rounded-lg">
        <summary className="flex items-center gap-2 cursor-pointer text-text-muted hover:text-white transition-colors p-4">
          <span className="font-heading text-sm uppercase tracking-wide">
            View All Steps ({steps.length})
          </span>
          <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform" />
        </summary>
        <div className="p-4 pt-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {steps.map((s, index) => (
            <button
              key={index}
              onClick={() => setCurrentStep(index)}
              className={cn(
                "text-left p-3 flex items-center gap-3 transition-colors rounded border",
                index === currentStep
                  ? "bg-primary/10 border-primary"
                  : "bg-surface/50 border-border hover:border-primary/50"
              )}
            >
              <div className={cn(
                "w-7 h-7 flex items-center justify-center font-mono text-xs rounded",
                completedSteps.includes(index)
                  ? "bg-success text-black"
                  : index === currentStep
                    ? "bg-primary text-white"
                    : "bg-surface-highlight text-text-muted"
              )}>
                {completedSteps.includes(index) ? (
                  <Check className="w-4 h-4" />
                ) : (
                  s.step || index + 1
                )}
              </div>
              <span className={cn(
                "text-xs line-clamp-2",
                index === currentStep ? "text-white" : "text-text-secondary"
              )}>
                {s.instruction}
              </span>
            </button>
          ))}
        </div>
      </details>
    </div>
  );
};

export default RepairWizard;
